import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  RotateCcw,
  ThumbsDown,
  Users,
} from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { useAuth } from '@/nucleo/auth/useAuth';
import { ehSoDre } from '@/nucleo/navegacao/apresentacao';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/base/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';
import { Label } from '@/ui/base/label';
import { SearchableSelect } from '@/ui/base/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Skeleton } from '@/ui/base/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { Textarea } from '@/ui/base/textarea';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar } from '@/ui/FilterBar';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { NextStepBanner } from '@/ui/NextStepBanner';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';

import { useDres } from './hooks/useDres';
import { useLotesAprovacaoSme } from './hooks/useAprovacaoSme';
import type { AprovacaoSmeCredenciadoLinha, StatusAprovacaoSmeCredenciado } from './types';

function competenciaAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

type MotivoDevolucaoCodigo =
  | 'DADOS_INCORRETOS'
  | 'FALTA_JUSTIFICATIVA'
  | 'DIVERGENCIA_VIGENCIA'
  | 'ALUNOS_INCONSISTENTES'
  | 'FALTAS_NAO_JUSTIFICADAS'
  | 'AJUSTE_INVALIDO'
  | 'ANEXO_FALTANTE'
  | 'OUTROS';

/** Catálogo de motivos de devolução — mesmo vocabulário do fluxo de Apontamentos, usado aqui para a devolução (individual ou em lote) de um lote SME. */
const MOTIVOS_DEVOLUCAO: { codigo: MotivoDevolucaoCodigo; label: string; hint?: string }[] = [
  {
    codigo: 'DADOS_INCORRETOS',
    label: 'Dados incorretos',
    hint: 'Quantidades, datas ou identificação errados',
  },
  {
    codigo: 'FALTA_JUSTIFICATIVA',
    label: 'Falta de justificativa',
    hint: 'Lançamento sem justificativa exigida',
  },
  {
    codigo: 'DIVERGENCIA_VIGENCIA',
    label: 'Divergência de vigência',
    hint: 'OS/Termo fora do período do apontamento',
  },
  {
    codigo: 'ALUNOS_INCONSISTENTES',
    label: 'Alunos inconsistentes',
    hint: 'Quantidade de alunos não bate com a OS',
  },
  {
    codigo: 'FALTAS_NAO_JUSTIFICADAS',
    label: 'Faltas não justificadas',
    hint: 'Faltas sem motivo registrado',
  },
  {
    codigo: 'AJUSTE_INVALIDO',
    label: 'Ajuste inválido',
    hint: 'Ajuste positivo/negativo sem suporte documental',
  },
  { codigo: 'ANEXO_FALTANTE', label: 'Anexo faltante', hint: 'Documento de suporte não enviado' },
  { codigo: 'OUTROS', label: 'Outros', hint: 'Descreva no detalhe' },
];

const MOTIVO_DETALHE_MIN = 5;

const STATUS_VARIANT: Record<
  StatusAprovacaoSmeCredenciado,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  AGUARDANDO: { label: 'Aguardando análise', variant: 'default' },
  APROVADO: { label: 'Aprovado', variant: 'secondary' },
  DEVOLVIDO: { label: 'Devolvido', variant: 'destructive' },
};

type LoteExibicao = {
  id: string;
  dreId: string;
  dreNome: string;
  dreCodigo: string | null;
  tipoPessoa: 'PF' | 'PJ';
  credenciados: AprovacaoSmeCredenciadoLinha[];
  statusAgregado: StatusAprovacaoSmeCredenciado;
  qtdPendentes: number;
  qtdDiasTotal: number;
  qtdAlunosTotal: number;
  enviadoMaisAntigo: string | null;
};

/**
 * Fila de aprovação SME sobre a competência apurada (financeiro) — distinta
 * do Resumo para Aprovação SME já existente (esse é institucional, este é o
 * fluxo operacional de aprovar/devolver por credenciado).
 *
 * QUEM É SÓ DRE NÃO VÊ O LOTE DE OUTRA DIRETORIA. O item continua visível a
 * ela (`tituloParaDre`, no menu) porque no protótipo (`SmeApontamentos.tsx`,
 * `useDresEscopo`/`isDrePuro`) ela tem uma visão própria, "Apontamentos da
 * minha DRE". Aqui não há como saber qual é a DRE de quem está olhando
 * (`UsuarioAutenticado`, `GET /eu`, sem esse campo), e mostrar o lote de todas
 * seria pior que não mostrar nenhum — mesma saída de `/sme/mensagens` e
 * `/sme/tratativas`. O gate vem antes de qualquer outro hook, num componente
 * fino, pela mesma razão de sempre: o conteúdo de verdade tem sua própria
 * cadeia de hooks, que não pode ficar depois de um retorno condicional.
 */
export function AprovacaoSmePage() {
  const { usuario } = useAuth();

  if (ehSoDre(usuario?.papeis)) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Aprovação SME"
          description="Lotes agrupados por DRE × tipo de pessoa, aguardando aprovação."
        />
        <EmptyState
          icon={Building2}
          title="Os lotes da sua diretoria ainda não estão disponíveis"
          description="Mostrar só os lotes da sua diretoria depende de uma consulta que ainda está sendo construída. Até lá a fila não é exibida: mostrar os lotes de todas as diretorias seria pior do que não mostrar nenhum."
        />
      </div>
    );
  }

  return <Conteudo />;
}

function Conteudo() {
  const navigate = useNavigate();
  // A mesma tela responde em dois cascos (`/sme/apontamentos` e
  // `/operacao/financeiro/aprovacao-sme`); o resumo abre no casco em que a
  // pessoa entrou, senão o portal SME a mandava para a Operação.
  const { pathname } = useLocation();
  const caminhoDoResumo = pathname.startsWith('/sme')
    ? '/sme/resumo-aprovacao'
    : '/operacao/financeiro/resumo-aprovacao-sme';
  const { data: dres } = useDres();
  const [mes, definirMes] = useState(competenciaAtual());
  const [dreFiltro, definirDreFiltro] = useState<string>('ALL');
  const [tipoPessoaFiltro, definirTipoPessoaFiltro] = useState<string>('ALL');
  const [statusFiltro, definirStatusFiltro] = useState<string>('AGUARDANDO');

  const { data, isLoading, isError, refetch } = useLotesAprovacaoSme(mes);

  const [devolverAlvo, definirDevolverAlvo] = useState<AprovacaoSmeCredenciadoLinha | null>(null);
  const [motivo, definirMotivo] = useState('');
  const [motivoCodigo, definirMotivoCodigo] = useState<MotivoDevolucaoCodigo | ''>('');

  const lotesExibicao: LoteExibicao[] = useMemo(
    () =>
      (data ?? []).map((lote) => {
        const dre = dres?.find((d) => d.id === lote.dreId);
        const pendentes = lote.credenciados.filter((c) => c.status === 'AGUARDANDO');
        const statusAgregado: StatusAprovacaoSmeCredenciado =
          pendentes.length > 0
            ? 'AGUARDANDO'
            : lote.credenciados.every((c) => c.status === 'APROVADO')
              ? 'APROVADO'
              : 'DEVOLVIDO';
        return {
          ...lote,
          dreCodigo: dre?.codigo ?? null,
          statusAgregado,
          qtdPendentes: pendentes.length,
          qtdDiasTotal: lote.credenciados.reduce((soma, c) => soma + c.diasDigitados, 0),
          qtdAlunosTotal: lote.credenciados.reduce((soma, c) => soma + c.alunos, 0),
          enviadoMaisAntigo: lote.credenciados.reduce<string | null>(
            (min, c) => (!min || c.dataEnvio < min ? c.dataEnvio : min),
            null,
          ),
        };
      }),
    [data, dres],
  );

  const lotesMestreFiltrados = useMemo(
    () =>
      lotesExibicao.filter((l) => {
        if (dreFiltro !== 'ALL' && l.dreId !== dreFiltro) return false;
        if (tipoPessoaFiltro !== 'ALL' && l.tipoPessoa !== tipoPessoaFiltro) return false;
        if (statusFiltro !== 'ALL' && l.statusAgregado !== statusFiltro) return false;
        return true;
      }),
    [lotesExibicao, dreFiltro, tipoPessoaFiltro, statusFiltro],
  );

  const lotesDetalhadosFiltrados = useMemo(
    () =>
      lotesExibicao.flatMap((lote) =>
        lote.credenciados
          .map((c) => ({
            ...c,
            dreId: lote.dreId,
            dreNome: lote.dreNome,
            dreCodigo: lote.dreCodigo,
            tipoPessoa: lote.tipoPessoa,
          }))
          .filter((c) => {
            if (dreFiltro !== 'ALL' && c.dreId !== dreFiltro) return false;
            if (tipoPessoaFiltro !== 'ALL' && c.tipoPessoa !== tipoPessoaFiltro) return false;
            if (statusFiltro !== 'ALL' && c.status !== statusFiltro) return false;
            return true;
          }),
      ),
    [lotesExibicao, dreFiltro, tipoPessoaFiltro, statusFiltro],
  );

  // KPIs do topo: universo total do mês, ignora os filtros de DRE/tipo/status.
  const totais = useMemo(() => {
    const todosCredenciados = lotesExibicao.flatMap((l) => l.credenciados);
    return {
      lotesPendentes: lotesExibicao.filter((l) => l.qtdPendentes > 0).length,
      aguardando: todosCredenciados.filter((c) => c.status === 'AGUARDANDO').length,
      aprovados: todosCredenciados.filter((c) => c.status === 'APROVADO').length,
      devolvidos: todosCredenciados.filter((c) => c.status === 'DEVOLVIDO').length,
    };
  }, [lotesExibicao]);

  const stats: StatItem[] = [
    { title: 'Lotes pendentes (DRE × PF/PJ)', value: totais.lotesPendentes, icon: Clock3 },
    { title: 'Credenciados aguardando', value: totais.aguardando, icon: Users },
    { title: 'Aprovados', value: totais.aprovados, icon: CheckCircle2 },
    { title: 'Devolvidos', value: totais.devolvidos, icon: ThumbsDown },
  ];

  // Aprovar e devolver ainda nao gravam: nao ha rota de aprovacao na API. Cada
  // acao so mostra o aviso de indisponibilidade, e nada muda na tela — nem o
  // status do credenciado, nem o dialogo de devolucao, que continua aberto com
  // o que foi preenchido. Padrao de `SlaAprovacaoSmePage`.
  function acaoIndisponivel() {
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  function confirmarDevolverIndividual() {
    if (!devolverAlvo || !motivoCodigo || motivo.trim().length < MOTIVO_DETALHE_MIN) return;
    acaoIndisponivel();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Aprovação SME"
        description={
          <>
            <GlossaryTerm termo="LOTE_SME" /> agrupados por <strong>DRE × tipo de pessoa</strong>.
            Aprove ou devolva uma <GlossaryTerm termo="REMESSA_MENSAL" esconderIcone /> inteira em
            um clique ou expanda para conferir credenciado por credenciado.
          </>
        }
      >
        <Button variant="outline" size="sm" onClick={() => navigate(`${caminhoDoResumo}/${mes}`)}>
          <ClipboardCheck className="mr-1 h-4 w-4" aria-hidden="true" /> Resumo institucional
        </Button>
      </PageHeader>

      {totais.lotesPendentes === 0 && totais.devolvidos > 0 && (
        <NextStepBanner
          variant="success"
          title="Sem lotes pendentes — fila no controle"
          description={`${totais.devolvidos} credenciado(s) devolvido(s) aguardam correção da DRE.`}
        />
      )}

      <StatsGrid stats={stats} columns="grid-cols-2 md:grid-cols-4" />

      <FilterBar
        fields={[
          { key: 'mes', label: 'Mês de referência', type: 'month', width: 'w-40' },
          {
            key: 'tipoPessoa',
            label: 'Tipo de pessoa',
            type: 'select',
            width: 'w-44',
            showAllOption: false,
            options: [
              { value: 'ALL', label: 'PF + PJ' },
              { value: 'PF', label: 'Pessoa Física' },
              { value: 'PJ', label: 'Pessoa Jurídica' },
            ],
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            width: 'w-48',
            showAllOption: false,
            options: [
              { value: 'ALL', label: 'Todos' },
              { value: 'AGUARDANDO', label: 'Aguardando análise' },
              { value: 'APROVADO', label: 'Aprovados' },
              { value: 'DEVOLVIDO', label: 'Devolvidos' },
            ],
          },
        ]}
        values={{ mes, tipoPessoa: tipoPessoaFiltro, status: statusFiltro }}
        onChange={(chave, valor) => {
          if (chave === 'mes') definirMes(valor);
          else if (chave === 'tipoPessoa') definirTipoPessoaFiltro(valor);
          else if (chave === 'status') definirStatusFiltro(valor);
        }}
        onClear={() => {
          definirMes(competenciaAtual());
          definirDreFiltro('ALL');
          definirTipoPessoaFiltro('ALL');
          definirStatusFiltro('AGUARDANDO');
        }}
        defaultValues={{ mes: competenciaAtual(), tipoPessoa: 'ALL', status: 'AGUARDANDO' }}
      >
        <div className="space-y-1 sm:w-56">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">DRE</Label>
          <SearchableSelect
            value={dreFiltro === 'ALL' ? null : dreFiltro}
            onChange={(v) => definirDreFiltro(v ?? 'ALL')}
            options={(dres ?? []).map((d) => ({
              value: d.id,
              label: d.nome ?? d.codigo ?? d.id,
              code: d.codigo ?? undefined,
            }))}
            placeholder="Todas as DREs"
            searchPlaceholder="Buscar por código ou nome..."
            clearable={false}
          />
        </div>
      </FilterBar>

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <Tabs defaultValue="lote">
          <TabsList>
            <TabsTrigger value="lote">
              Por lote DRE × PF/PJ
              <Badge variant="secondary" className="ml-2">
                {lotesMestreFiltrados.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="detalhado">
              Por credenciado
              <Badge variant="secondary" className="ml-2">
                {lotesDetalhadosFiltrados.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lote" className="mt-3 space-y-3">
            {lotesMestreFiltrados.length === 0 ? (
              <Card>
                <CardContent className="p-6">
                  <EmptyState
                    icon={ClipboardCheck}
                    title="Nenhum lote para esta combinação"
                    description="Ajuste filtros, mês ou aguarde envios das DREs."
                  />
                </CardContent>
              </Card>
            ) : (
              lotesMestreFiltrados.map((lote) => (
                <LoteSmeCard key={lote.id} lote={lote} onAcaoIndisponivel={acaoIndisponivel} />
              ))
            )}
          </TabsContent>

          <TabsContent value="detalhado" className="mt-3 space-y-3">
            {lotesDetalhadosFiltrados.length === 0 ? (
              <Card>
                <CardContent className="p-6">
                  <EmptyState
                    icon={ClipboardCheck}
                    title="Nenhum credenciado para esta combinação"
                    description="Ajuste filtros, mês ou aguarde envios das DREs."
                  />
                </CardContent>
              </Card>
            ) : (
              lotesDetalhadosFiltrados.map((c) => {
                const status = STATUS_VARIANT[c.status];
                const pendente = c.status === 'AGUARDANDO';
                return (
                  <Card key={c.credenciadoId}>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{c.credenciadoNome}</span>
                            <Badge variant="outline">{c.tipoPessoa}</Badge>
                            {c.dreCodigo && <Badge variant="secondary">DRE {c.dreCodigo}</Badge>}
                            <Badge variant={status.variant}>{status.label}</Badge>
                          </div>
                          <div className="mt-1 text-sm text-muted-foreground">
                            CPF/CNPJ: {c.cpfCnpj}
                            {c.dataEnvio && (
                              <>
                                {' '}
                                · Enviado em{' '}
                                {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(
                                  new Date(c.dataEnvio),
                                )}
                              </>
                            )}
                          </div>
                          <div className="mt-2 text-sm">
                            <span className="font-medium">{c.diasDigitados}</span> dia(s)
                            digitado(s) · <span className="font-medium">{c.alunos}</span> aluno(s)
                            (acumulado)
                          </div>
                        </div>

                        {pendente && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                definirDevolverAlvo(c);
                                definirMotivo('');
                                definirMotivoCodigo('');
                              }}
                            >
                              <RotateCcw className="mr-1 h-4 w-4" aria-hidden="true" /> Devolver
                            </Button>
                            <ConfirmDialog
                              trigger={
                                <Button size="sm">
                                  <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" />{' '}
                                  Aprovar
                                </Button>
                              }
                              title="Aprovar credenciado?"
                              description={`Aprova ${c.diasDigitados} dia(s) de ${c.credenciadoNome}. O lote será liberado para o DTP/Financeiro.`}
                              confirmLabel="Aprovar"
                              onConfirm={acaoIndisponivel}
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Modal de devolução individual (aba "Por credenciado") */}
      <Dialog
        open={!!devolverAlvo}
        onOpenChange={(open) => {
          if (!open) {
            definirDevolverAlvo(null);
            definirMotivo('');
            definirMotivoCodigo('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
              Devolver credenciado para DRE
            </DialogTitle>
            <DialogDescription>
              {devolverAlvo && (
                <>
                  O lote de <strong>{devolverAlvo.credenciadoNome}</strong> (
                  {devolverAlvo.diasDigitados} dia(s)) voltará para a DRE para correção. O motivo
                  padronizado fica registrado na auditoria imutável.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <AvisoDevolucaoNaoRegistrada />
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="motivo-codigo-individual">Motivo padronizado (obrigatório)</Label>
              <Select
                value={motivoCodigo}
                onValueChange={(v) => definirMotivoCodigo(v as MotivoDevolucaoCodigo)}
              >
                <SelectTrigger id="motivo-codigo-individual">
                  <SelectValue placeholder="Selecione o motivo da devolução" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS_DEVOLUCAO.map((m) => (
                    <SelectItem key={m.codigo} value={m.codigo}>
                      <div className="flex flex-col">
                        <span>{m.label}</span>
                        {m.hint && <span className="text-xs text-muted-foreground">{m.hint}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motivo-devolucao-individual">
                Detalhe (obrigatório, mínimo {MOTIVO_DETALHE_MIN} caracteres)
              </Label>
              <Textarea
                id="motivo-devolucao-individual"
                rows={4}
                value={motivo}
                onChange={(e) => definirMotivo(e.target.value)}
                placeholder="Ex.: Divergência nos dias 12 e 13 — verificar com a escola."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                definirDevolverAlvo(null);
                definirMotivo('');
                definirMotivoCodigo('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmarDevolverIndividual}
              disabled={!motivoCodigo || motivo.trim().length < MOTIVO_DETALHE_MIN}
            >
              Devolver para DRE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Aviso FIXO no dialogo de devolucao, alem do aviso passageiro do botao: aviso
 * que some deixa quem audita uma captura de tela concluir que o lote voltou
 * para a DRE. Mesmo padrao de `/sme/revisoes`.
 */
function AvisoDevolucaoNaoRegistrada() {
  return (
    <Alert>
      <AlertTitle>A devolução ainda não é registrada</AlertTitle>
      <AlertDescription>
        Aprovar e devolver entram em funcionamento junto com o restante do ciclo de medição. Nada do
        que for preenchido aqui é gravado nem enviado à DRE.
      </AlertDescription>
    </Alert>
  );
}

function LoteSmeCard({
  lote,
  onAcaoIndisponivel,
}: {
  lote: LoteExibicao;
  /** Aprovar e devolver ainda nao gravam: so o aviso de indisponibilidade. */
  onAcaoIndisponivel: () => void;
}) {
  const [aberto, definirAberto] = useState(false);
  const [devolverAberto, definirDevolverAberto] = useState(false);
  const [motivo, definirMotivo] = useState('');
  const [motivoCodigo, definirMotivoCodigo] = useState<MotivoDevolucaoCodigo | ''>('');

  const status = STATUS_VARIANT[lote.statusAgregado];
  const temPendentes = lote.qtdPendentes > 0;

  // Um aviso so por acao em lote — antes o laco chamava a gravacao uma vez por
  // credenciado. O dialogo de devolucao continua aberto: fecha-lo daria a
  // entender que o lote voltou para a DRE.
  function devolverTodos() {
    if (!motivoCodigo || motivo.trim().length < MOTIVO_DETALHE_MIN) return;
    onAcaoIndisponivel();
  }

  return (
    <Card className={temPendentes ? 'border-primary/40' : undefined}>
      <CardContent className="space-y-3 p-4">
        <Collapsible open={aberto} onOpenChange={definirAberto}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="font-semibold">
                  DRE {lote.dreCodigo ?? '—'} · {lote.dreNome}
                </span>
                <Badge variant="outline">{lote.tipoPessoa}</Badge>
                <Badge variant={status.variant}>{status.label}</Badge>
                <span className="text-xs text-muted-foreground">
                  <GlossaryTerm termo="LOTE_SME" esconderIcone />
                </span>
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                {lote.credenciados.length} credenciado(s)
                {temPendentes && (
                  <>
                    {' · '}
                    <span className="font-medium text-primary">
                      {lote.qtdPendentes} pendente(s)
                    </span>
                  </>
                )}
                {' · '}
                <span className="font-medium">{lote.qtdDiasTotal}</span> dia(s) ·{' '}
                <span className="font-medium">{lote.qtdAlunosTotal}</span> aluno(s) acumulado
                {lote.enviadoMaisAntigo && (
                  <>
                    {' · Enviado desde '}
                    {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(
                      new Date(lote.enviadoMaisAntigo),
                    )}
                  </>
                )}
              </div>
            </div>

            {temPendentes && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => definirDevolverAberto(true)}>
                  <RotateCcw className="mr-1 h-4 w-4" aria-hidden="true" /> Devolver lote
                </Button>
                <ConfirmDialog
                  trigger={
                    <Button size="sm">
                      <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" /> Aprovar lote (
                      {lote.qtdPendentes})
                    </Button>
                  }
                  title="Aprovar lote SME?"
                  description={`Aprova ${lote.qtdPendentes} credenciado(s) da DRE ${lote.dreCodigo} (${lote.tipoPessoa}). A decisão é registrada por credenciado para preservar a trilha de auditoria, mas conta como uma única decisão de lote.`}
                  confirmLabel={`Aprovar ${lote.qtdPendentes} credenciado(s)`}
                  onConfirm={onAcaoIndisponivel}
                />
              </div>
            )}
          </div>

          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              {aberto ? (
                <ChevronDown className="mr-1 h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRight className="mr-1 h-4 w-4" aria-hidden="true" />
              )}
              {aberto ? 'Ocultar' : 'Conferir'} {lote.credenciados.length} credenciado(s)
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2">
            <div className="divide-y rounded-md border">
              {lote.credenciados.map((c) => {
                const cStatus = STATUS_VARIANT[c.status];
                const cPendente = c.status === 'AGUARDANDO';
                return (
                  <div
                    key={c.credenciadoId}
                    className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{c.credenciadoNome}</div>
                      <div className="text-xs text-muted-foreground">
                        CPF/CNPJ {c.cpfCnpj} · {c.diasDigitados} dia(s) · {c.alunos} aluno(s)
                      </div>
                    </div>
                    <Badge variant={cStatus.variant} className="text-xs">
                      {cStatus.label}
                    </Badge>
                    {cPendente && (
                      <ConfirmDialog
                        trigger={
                          <Button size="sm" variant="ghost">
                            <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" /> Aprovar só
                            este
                          </Button>
                        }
                        title="Aprovar somente este credenciado?"
                        description={`Mantém os demais credenciados da DRE ${lote.dreCodigo} pendentes.`}
                        confirmLabel="Aprovar credenciado"
                        onConfirm={onAcaoIndisponivel}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>

      <Dialog
        open={devolverAberto}
        onOpenChange={(o) => {
          if (!o) {
            definirDevolverAberto(false);
            definirMotivo('');
            definirMotivoCodigo('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
              Devolver lote SME para a DRE
            </DialogTitle>
            <DialogDescription>
              <strong>{lote.qtdPendentes} credenciado(s)</strong> da DRE {lote.dreCodigo} (
              {lote.tipoPessoa}) voltarão para correção. O mesmo motivo será registrado em cada
              credenciado.
            </DialogDescription>
          </DialogHeader>
          <AvisoDevolucaoNaoRegistrada />
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="motivo-codigo-lote">Motivo padronizado (obrigatório)</Label>
              <Select
                value={motivoCodigo}
                onValueChange={(v) => definirMotivoCodigo(v as MotivoDevolucaoCodigo)}
              >
                <SelectTrigger id="motivo-codigo-lote">
                  <SelectValue placeholder="Selecione o motivo da devolução" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS_DEVOLUCAO.map((m) => (
                    <SelectItem key={m.codigo} value={m.codigo}>
                      <div className="flex flex-col">
                        <span>{m.label}</span>
                        {m.hint && <span className="text-xs text-muted-foreground">{m.hint}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="motivo-lote">
                Detalhe (obrigatório, mínimo {MOTIVO_DETALHE_MIN} caracteres)
              </Label>
              <Textarea
                id="motivo-lote"
                rows={4}
                value={motivo}
                onChange={(e) => definirMotivo(e.target.value)}
                placeholder="Ex.: Divergência na quantidade de alunos cadeirantes nos dias 12 e 13."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                definirDevolverAberto(false);
                definirMotivo('');
                definirMotivoCodigo('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={devolverTodos}
              disabled={motivo.trim().length < MOTIVO_DETALHE_MIN || !motivoCodigo}
            >
              Devolver lote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default AprovacaoSmePage;
